const destinations = [
  {
    name: "Mexico",
    context: "Family reunions in Chihuahua and Juárez, business in Mexico City and Monterrey, medical and dental tourism, holiday travel",
    urgency: "Family events and holidays have fixed dates — passports must be ready well in advance",
    icon: "ri-map-pin-2-line",
    flag: "MX",
  },
  {
    name: "Germany",
    context: "Kirtland AFB PCS assignments to Ramstein and Spangdahlem; Sandia Labs research collaborations in Stuttgart and Munich",
    urgency: "PCS orders and conference invitations often arrive with 14–30 days notice",
    icon: "ri-map-pin-2-line",
    flag: "DE",
  },
  {
    name: "Japan & South Korea",
    context: "Space Force and AFRL joint assignments in the Pacific; UNM engineering exchange programs; semiconductor and tech partnerships",
    urgency: "Deployment and TDY orders have immovable report dates",
    icon: "ri-map-pin-2-line",
    flag: "JP",
  },
  {
    name: "Spain & Latin America",
    context: "UNM study-abroad programs in Madrid and Barcelona; heritage tourism in Spain; research collaborations throughout Latin America",
    urgency: "Study-abroad application deadlines and academic calendars are fixed and non-negotiable",
    icon: "ri-map-pin-2-line",
    flag: "ES",
  },
  {
    name: "Canada",
    context: "Business travel to Toronto and Vancouver; family vacations to Banff and the Canadian Rockies; conference travel",
    urgency: "Summer vacation bookings and conference dates are fixed 2–6 weeks out",
    icon: "ri-map-pin-2-line",
    flag: "CA",
  },
  {
    name: "United Kingdom",
    context: "Sandia and AFRL research exchanges with UK defence laboratories; UNM faculty collaborations; Balloon Fiesta international media travel",
    urgency: "Research timelines and media accreditation deadlines are fixed and non-negotiable",
    icon: "ri-map-pin-2-line",
    flag: "GB",
  },
];

const commonSituations = [
  {
    stat: "62%",
    label: "of Albuquerque expedited requests are military-related, cross-border family travel, or research/conference travel",
  },
  {
    stat: "2–4 Weeks",
    label: "average notice Albuquerque travelers have before a planned international trip, PCS, or conference",
  },
  {
    stat: "Mar–Oct",
    label: "peak season when PCS cycles, UNM study-abroad deadlines, and summer family travel to Mexico and Europe collide",
  },
  {
    stat: "48 Hours",
    label: "shortest turnaround we have accommodated for a Kirtland AFB family with emergency deployment orders",
  },
];

export default function AlbuquerqueDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Albuquerque Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Central New Mexico clients. Understanding local travel behavior is part of why our document review catches issues before they derail your PCS, research trip, or family reunion south of the border.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                {d.context}
              </p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">
                  {d.urgency}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Southwest Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across the Southwest with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/el-paso" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                El Paso <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/tucson" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Tucson <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/denver" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Denver <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}